import { useMemo, type KeyboardEventHandler, type MouseEventHandler, type ReactNode, type Ref } from 'react'
import type { PressableProps } from 'react-native'

type TPressableProps = PressableProps & {
  dataSet?: Record<string, string | boolean>
  onClick?: MouseEventHandler
  ref?: Ref<HTMLDivElement>
  children?: ReactNode
}

const isDivAttribute = (prop: string, divElement: HTMLDivElement): boolean =>
  prop in divElement || prop.toLowerCase() in divElement || prop.startsWith('aria-')

export const Pressable = ({
  onClick,
  onHoverIn,
  onHoverOut,
  onPress,
  onPressIn,
  onPressOut,
  dataSet,
  testID,
  disabled,
  ref,
  role = 'button',
  tabIndex,
  children,
  ...otherProps
}: TPressableProps) => {
  const isDisabled = Boolean(disabled)

  const dataSetAttributes = useMemo(() => {
    const newDataSet: { [key: string]: string | boolean } = {}
    Object.entries(dataSet || {}).forEach(([key, value]) => {
      newDataSet[`data-${key}`] = value
    })

    return newDataSet
  }, [dataSet])

  // Filter out props that are not valid for a div element (keep aria-* for a11y)
  const attributesForDiv: Record<string, unknown> = {}
  const divElement = useMemo(() => document.createElement('div'), [])
  const otherPropsByKey: Record<string, unknown> = otherProps
  for (const prop in otherProps) {
    if (isDivAttribute(prop, divElement)) {
      attributesForDiv[prop] = otherPropsByKey[prop]
    }
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (event.defaultPrevented || isDisabled) return

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      // Space scrolls the page unless prevented (APG button / radio)
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault()
      }
      // @ts-expect-error - event given to onPress is a web event not a native one
      onPress?.(event)
    }
  }

  return (
    <div
      {...attributesForDiv}
      {...dataSetAttributes}
      data-test-id={testID}
      data-testid={testID}
      ref={ref}
      role={role}
      tabIndex={tabIndex ?? (isDisabled ? -1 : 0)}
      aria-disabled={isDisabled || undefined}
      // @ts-expect-error - hack cross platform
      onClick={isDisabled ? undefined : onClick || onPress || undefined}
      // @ts-expect-error - hack cross platform
      onMouseDown={onPressIn}
      // @ts-expect-error - hack cross platform
      onMouseUp={onPressOut}
      // @ts-expect-error - hack cross platform
      onMouseEnter={onHoverIn}
      // @ts-expect-error - hack cross platform
      onMouseLeave={onHoverOut}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  )
}

Pressable.displayName = 'Pressable'
